import React from "react";
import { Box, Heading, Text, VStack, Icon, SimpleGrid } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { FaWrench } from "react-icons/fa";

const QuickFixesPage = () => {
  return (
    <Box minH="100vh" bg="white">
      {/* Hero Section */}
      <Box
        bg="white"
        py={{ base: 16, md: 24 }}
        px={{ base: 6, md: 12 }}
        textAlign="center"
      >
        <Heading
          as="h1"
          fontSize={{ base: "4xl", md: "6xl" }}
          fontWeight="800"
          color="gray.900"
          mb={6}
          fontFamily="'Work Sans', sans-serif"
        >
          Quick Fixes for Common Home Issues
        </Heading>
        <Text
          fontSize={{ base: "lg", md: "xl" }}
          color="gray.600"
          maxW="2xl"
          mx="auto"
        >
          Got a minor home problem that needs a quick solution? Explore our
          quick-fix guides to help you handle common home issues without the
          need for a professional.
        </Text>
      </Box>

      {/* Quick Fix Cards Section */}
      <Box bg="gray.50" py={{ base: 12, md: 20 }} px={{ base: 6, md: 12 }}>
        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={8}
          maxW="1200px"
          mx="auto"
        >
          {/* Quick Fix Card 1: Leaky Faucet */}
          <Link to="/quick-fix/leaky-faucet" style={{ textDecoration: "none" }}>
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              boxShadow="sm"
              transition="all 0.3s ease"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              cursor="pointer"
            >
              <VStack align="start" spacing={4}>
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="full"
                  display="inline-flex"
                >
                  <Icon as={FaWrench} w={8} h={8} color="orange.500" />
                </Box>
                <Heading fontSize="2xl" fontWeight="700" color="gray.900">
                  How to Fix a Leaky Faucet
                </Heading>
                <Text color="gray.600" fontSize="md">
                  A leaky faucet can waste gallons of water over time. Turn off
                  the water supply, remove the handle, and replace the washer or
                  O-ring inside. Reassemble the parts and check for leaks.
                </Text>
              </VStack>
            </Box>
          </Link>

          {/* Quick Fix Card 2: Unclogging a Sink */}
          <Link to="/quick-fix/unclog-sink" style={{ textDecoration: "none" }}>
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              boxShadow="sm"
              transition="all 0.3s ease"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              cursor="pointer"
            >
              <VStack align="start" spacing={4}>
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="full"
                  display="inline-flex"
                >
                  <Icon as={FaWrench} w={8} h={8} color="orange.500" />
                </Box>
                <Heading fontSize="2xl" fontWeight="700" color="gray.900">
                  Unclogging a Sink
                </Heading>
                <Text color="gray.600" fontSize="md">
                  Mix baking soda and vinegar, pour it down the drain, and let
                  it sit for 15 minutes. Flush with hot water. Repeat as needed
                  or use a plunger for tougher clogs.
                </Text>
              </VStack>
            </Box>
          </Link>

          {/* Quick Fix Card 3: Patching Small Wall Holes */}
          <Link
            to="/quick-fix/patch-drywall"
            style={{ textDecoration: "none" }}
          >
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              boxShadow="sm"
              transition="all 0.3s ease"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              cursor="pointer"
            >
              <VStack align="start" spacing={4}>
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="full"
                  display="inline-flex"
                >
                  <Icon as={FaWrench} w={8} h={8} color="orange.500" />
                </Box>
                <Heading fontSize="2xl" fontWeight="700" color="gray.900">
                  Patching Small Holes in Drywall
                </Heading>
                <Text color="gray.600" fontSize="md">
                  Apply spackle to the hole using a putty knife, smooth it out,
                  and let it dry. Sand it down and apply primer and paint to
                  match the wall color.
                </Text>
              </VStack>
            </Box>
          </Link>

          {/* Quick Fix Card 4: Resetting a Tripped Breaker */}
          <Link
            to="/quick-fix/reset-breaker"
            style={{ textDecoration: "none" }}
          >
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              boxShadow="sm"
              transition="all 0.3s ease"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              cursor="pointer"
            >
              <VStack align="start" spacing={4}>
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="full"
                  display="inline-flex"
                >
                  <Icon as={FaWrench} w={8} h={8} color="orange.500" />
                </Box>
                <Heading fontSize="2xl" fontWeight="700" color="gray.900">
                  Resetting a Tripped Breaker
                </Heading>
                <Text color="gray.600" fontSize="md">
                  Locate the breaker box and identify the tripped switch. Turn
                  it off and then back on. If the breaker trips repeatedly,
                  consult a professional.
                </Text>
              </VStack>
            </Box>
          </Link>
        </SimpleGrid>
      </Box>

      {/* Final Message Section */}
      <Box
        bg="white"
        py={{ base: 12, md: 16 }}
        px={{ base: 6, md: 12 }}
        textAlign="center"
      >
        <Text
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="600"
          color="gray.800"
          maxW="3xl"
          mx="auto"
        >
          With these tips, we hope to empower you to handle common home issues
          with confidence. Let's make home improvement easier, one fix at a
          time!
        </Text>
      </Box>
    </Box>
  );
};

export default QuickFixesPage;
